import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {useCallback, useMemo, useState} from 'react';
import {categoryGroupsApi} from '../api';
import {
  CategoryGroupCategoriesFilter,
  CategoryGroupCategoriesWithMetaModel,
  CategoryGroupModel,
} from '../model';

type UseCategoryGroupByIdResult = {
  getCategoryGroup: UseQueryResult<CategoryGroupModel, Error>;
  getInfiniteCategories: UseInfiniteQueryResult<InfiniteData<CategoryGroupCategoriesWithMetaModel>, Error>;
  filters: CategoryGroupCategoriesFilter;
  updateFilter: (newFilter: Partial<CategoryGroupCategoriesFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useCategoryGroupById = (
  categoryGroupId: string,
  initialFilter: CategoryGroupCategoriesFilter = {},
): UseCategoryGroupByIdResult => {
  const [filters, setFilters] = useState<CategoryGroupCategoriesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const getCategoryGroup = useQuery({
    queryKey: ['categoryGroups', categoryGroupId],
    queryFn: () => categoryGroupsApi.getCategoryGroupById(categoryGroupId),
    enabled: Boolean(categoryGroupId),
  });

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteCategories = useInfiniteQuery<
    CategoryGroupCategoriesWithMetaModel,
    Error,
    InfiniteData<CategoryGroupCategoriesWithMetaModel>,
    ['categoryGroups', string, 'categories', 'infinite', Omit<CategoryGroupCategoriesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['categoryGroups', categoryGroupId, 'categories', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return categoryGroupsApi.getCategoryGroupCategories(categoryGroupId, {
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage && lastPage.pageInfo.endCursor
        ? lastPage.pageInfo.endCursor
        : undefined;
    },
    enabled: Boolean(categoryGroupId),
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<CategoryGroupCategoriesFilter>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
    }));
  }, []);

  return {
    getCategoryGroup,
    getInfiniteCategories,
    filters,
    updateFilter,
  };
};
